import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, Button, Drawer, Grid } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { OrdersContext } from '../../../lib/contexts/OrdersContext';
import { UserContext } from '../../../lib/contexts/UserContext';
import EditAddressPopUp from './subComponents/EditAddressPopUp';
import CheckIcon from '@mui/icons-material/Check';
import useUserAddress from '../../../lib/hooks/useUserAddress';

const AddressEditCard = ({ deliveryID = null, onClose }) => {
  if (deliveryID === null) {
    return <div>No delivery ID provided</div>;
  }

  const isMobileView = useMediaQuery('(max-width: 600px)');
  const {
    state: { ordersData },
    actions: { updateOrderAddress },
  } = useContext(OrdersContext);
  const {
    state: { firstName, lastName },
  } = useContext(UserContext);
  const [thisOrder, setThisOrder] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddAddress, setIsAddAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressToEdit, setAddressToEdit] = useState(null);

  const previousAddressRef = useRef(selectedAddress);

  const { data: userAddresses, isLoading, error } = useUserAddress();

  const formatAddress = (location) => {
    const { address, address_2, city, state, zip, country } = location;
    return (
      <Grid sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div>
          {address}
          {address_2 ? `, ${address_2}` : ''}
        </div>
        <div>
          {city} {state}
        </div>
        <div>
          {country} {zip}
        </div>
      </Grid>
    );
  };

  const capitalizeWords = (str) => {
    return str.replace(/\b\w+\b/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  };

  const formatUserAddress = (userAddress) => {
    const { address, address_2, city, state, zip, country } = userAddress;
    return (
      <Grid sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div>
          {capitalizeWords(address)}
          {address_2 ? `, ${capitalizeWords(address_2)}` : ''}
        </div>
        <div>
          {capitalizeWords(city)}, {capitalizeWords(state)}
        </div>
        <div>
          {capitalizeWords(country)} {zip}
        </div>
      </Grid>
    );
  };

  useEffect(() => {
    for (const order of ordersData) {
      if (order.id === deliveryID) {
        setThisOrder(order);
      }
    }
  }, [ordersData]);

  useEffect(() => {
    if (!thisOrder || isLoading || error) return;

    let formattedUserAddresses = [];
    if (userAddresses && Array.isArray(userAddresses.locations)) {
      formattedUserAddresses = userAddresses.locations;
    }

    const initialAddresses = thisOrder?.location ? [thisOrder.location] : [];
    const allAddresses = [...initialAddresses, ...formattedUserAddresses];
    setAddresses(allAddresses);

    if (allAddresses.length > 0) {
      if (initialAddresses.length > 0) {
        setSelectedAddress(allAddresses[0]);
      }
    }
  }, [thisOrder, userAddresses, isLoading, error]);

  useEffect(() => {
    if (selectedAddress && deliveryID) {
      const order = ordersData.find((order) => order.id === deliveryID);
      if (order && order?.location?.id !== selectedAddress.id) {
        if (previousAddressRef.current !== selectedAddress.id) {
          const items = order.order_items.map((item) => ({
            store_item_id: item.store_item_id,
            quantity: item.quantity,
          }));
          updateOrderAddress(deliveryID, selectedAddress.id, items);
          previousAddressRef.current = selectedAddress.id;
        }
      }
    }
  }, [selectedAddress]);

  const handleDrawerOpen = (addAddress = false, address = null) => {
    setIsAddAddress(addAddress);
    setAddressToEdit(address);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleAddressSelect = async (address) => {
    previousAddressRef.current = selectedAddress;
    setSelectedAddress(address);

    const result = await updateOrderAddress(deliveryID, address.id);
    if (!result.success) {
      setSelectedAddress(previousAddressRef.current);
    }
  };

  const handleUpdateOrderAddress = () => {
    if (selectedAddress && deliveryID) {
      const order = ordersData.find((order) => order.id === deliveryID);
      if (order && order.location.id !== selectedAddress.id) {
        const items = order.order_items.map((item) => ({
          store_item_id: item.store_item_id,
          quantity: item.quantity,
        }));
        updateOrderAddress(deliveryID, selectedAddress.id, items);
      }
    }
  };

  const uniqueAddresses = [];
  const seenIds = new Set();

  addresses?.forEach((address) => {
    if (!seenIds.has(address.id)) {
      uniqueAddresses.push(address);
      seenIds.add(address.id);
    }
  });

  return (
    <Box
      id="address-edit-card"
      sx={{
        display: 'flex',
        width: isMobileView ? '400px' : '440px',
        padding: '24px 32px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        flexShrink: 0,
        borderLeft: '1px solid var(--color4, #EBE8F3)',
        background: 'var(--color1, #FDFDFD)',
        // boxShadow:
        //   '0px 4px 5px 0px rgba(0, 0, 0, 0.02), 0px 1px 10px 0px rgba(0, 0, 0, 0.02), 0px 2px 4px -1px rgba(17, 17, 17, 0.03)',
      }}
    >
      <Box
        id="address-edit-card-content-body"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '16px',
          alignSelf: 'stretch',
        }}
      >
        <Box
          id="main-header"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--2-5, 10px)',
          }}
        >
          <Box
            id="header-title"
            sx={{
              display: 'flex',
              width: '376px',
              alignItems: 'flex-start',
              gap: '10px',
            }}
          >
            <Box
              id="back-button-container"
              sx={{
                display: 'flex',
                width: '24px',
                height: '38px',
                alignItems: 'center',
                gap: '10px',
                flexShrink: 0,
              }}
            >
              <ArrowBackIcon
                onClick={onClose}
                style={{
                  height: '24px',
                  flex: '1 0 0',
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Box
              id="title-box"
              sx={{
                display: 'flex',
                width: '309px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0,
              }}
            >
              <Box
                id="title"
                sx={{
                  display: 'flex',
                  padding: '4px 0px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  alignSelf: 'stretch',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #1C124D)',
                    textAlign: 'center',
                    fontFamily: 'Montserrat',
                    fontSize: '24px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '125%', // 30px
                  }}
                >
                  Your Addresses
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          id="addresses-container"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Button
            id="add-address-button"
            sx={{
              display: 'flex',
              width: '311px',
              height: 'var(--4, 44px)',
              padding: 'var(--0, 0px) var(--4, 18px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--4, 18px)',
              borderRadius: 'var(--4, 9px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
            }}
            onClick={() => handleDrawerOpen(true)}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 700,
              }}
            >
              Add New Address
            </Typography>
          </Button>
          {uniqueAddresses?.map((address, index) => (
            <Box
              key={index}
              id="address"
              sx={{
                display: 'flex',
                padding: '24px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '24px',
                alignSelf: 'stretch',
                borderRadius: '8px',
                border:
                  selectedAddress === address
                    ? '3px solid var(--Tangelo-Matcha-matcha9, #00AD8C)'
                    : '2px solid var(--color6, #D6D2E7)',
                background: selectedAddress === address ? 'var(--Tangelo-Matcha-matcha1, #F9FEFC)' : '#FFF',
              }}
            >
              <Box
                id="address-info-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 'var(--3-5, 16px)',
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  id="address-info"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '24px',
                    alignSelf: 'stretch',
                  }}
                >
                  <Box
                    id="info"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: '16px',
                      alignSelf: 'stretch',
                    }}
                  >
                    <Box
                      id="name"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '13px',
                        alignSelf: 'stretch',
                      }}
                    >
                      <Typography
                        sx={{
                          flex: '1 0 0',
                          color: 'var(--color12, #1C124D)',
                          leadingTrim: 'both',
                          textEdge: 'cap',
                          fontFamily: 'Montserrat',
                          fontSize: '18px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          lineHeight: 'normal',
                        }}
                      >
                        {`${firstName} ${lastName}`}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        alignSelf: 'stretch',
                        color: 'var(--color12, #1C124D)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Montserrat',
                        fontSize: '18px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                      }}
                    >
                      {formatAddress(address)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                id="address-box-buttons"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '16px',
                  alignSelf: 'stretch',
                }}
              >
                {selectedAddress === address ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <Button
                      id="selected-address-button"
                      sx={{
                        display: 'flex',
                        width: '100%',
                        height: 'var(--3, 36px)',
                        padding: 'var(--0, 0px) var(--3, 13px)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 'var(--3, 13px)',
                        borderRadius: 'var(--3, 7px)',
                        border: 'var(--1, 2px) solid var(--Tangelo-Matcha-matcha6, #A2E2D4)',
                        background: 'var(--Tangelo-Matcha-matcha6, #A2E2D4)',
                      }}
                      onClick={() => handleDrawerOpen(false, address)}
                    >
                      <CheckIcon
                        sx={{
                          width: '20px',
                          height: '20px',
                        }}
                      />
                      <Typography
                        sx={{
                          color: 'var(--color12, #1C124D)',
                          textAlign: 'center',
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: 700,
                        }}
                      >
                        Selected
                      </Typography>
                    </Button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                    <Button
                      sx={{
                        display: 'flex',
                        width: '100%',
                        height: 'var(--3, 36px)',
                        padding: 'var(--0, 0px) var(--3, 13px)',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 'var(--3, 13px)',
                        borderRadius: 'var(--3, 7px)',
                        border: 'var(--1, 2px) solid var(--color12, #1C124D)',
                        background: 'var(--color1, #FDFDFD)',
                      }}
                      onClick={() => {
                        handleAddressSelect(address);
                        handleUpdateOrderAddress();
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--color12, #1C124D)',
                          textAlign: 'center',
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: 700,
                        }}
                      >
                        Select this Address
                      </Typography>
                    </Button>
                  </div>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
      >
        <EditAddressPopUp
          deliveryID={deliveryID}
          onClose={handleDrawerClose}
          addAddress={isAddAddress}
          thisOrder={thisOrder}
          addressToEdit={addressToEdit}
        />
      </Drawer>
    </Box>
  );
};

export default AddressEditCard;
